import {
  ActionGroup,
  Alert,
  Button,
  FormAlert,
  Modal,
  ModalVariant,
  Title,
  Select,
  SelectOption,
  SelectVariant,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'

import { useVisitDefinitions } from '@app/domain/visit/visit.hooks'

interface Props {
  isModalOpen: boolean
  selectedDefinitionId?: string
  onSave: (definitionId: string) => void
  onCancel: () => void
  title: string
}

export const SelectConfigurationDefinitionModal = ({
  isModalOpen,
  selectedDefinitionId,
  onSave,
  onCancel,
  title,
}: Props) => {
  const { definitions } = useVisitDefinitions()
  const [selectedId, setSelectedId] = useState<string | undefined>(
    selectedDefinitionId,
  )
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    setSelectedId(selectedDefinitionId)
  }, [selectedDefinitionId])

  const handleSave = useCallback(() => {
    if (selectedId) {
      onSave(selectedId)
    }
  }, [selectedId, onSave])

  const handleToggle = useCallback(() => setIsOpen((prev) => !prev), [])
  const handleSelect = useCallback((_event, value) => {
    setSelectedId(value as string)
    setIsOpen(false)
  }, [])

  return (
    <Modal
      variant={ModalVariant.medium}
      title={title}
      isOpen={isModalOpen}
      onClose={onCancel}
    >
      {selectedId == null && (
        <FormAlert>
          <Alert
            variant="warning"
            title="No configuration has been selected!"
            aria-live="polite"
            isInline
          />
        </FormAlert>
      )}

      <Title headingLevel="h3" size="lg">
        Select Configuration for Interaction
      </Title>

      <Select
        variant={SelectVariant.single}
        isOpen={isOpen}
        onToggle={handleToggle}
        selections={selectedId}
        onSelect={handleSelect}
        menuAppendTo="parent"
        placeholderText="Select a configuration"
      >
        {definitions.map((defn, idx) => (
          <SelectOption key={idx} value={defn.id}>
            {defn.name}
          </SelectOption>
        ))}
      </Select>

      <ActionGroup style={{ float: 'right', marginTop: 10 }}>
        <Button
          style={{ marginRight: 5 }}
          key="cancel"
          variant="secondary"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          key="save"
          variant="primary"
          onClick={handleSave}
          isDisabled={!selectedId}
        >
          Save
        </Button>
      </ActionGroup>
    </Modal>
  )
}
